/* ==========================================================================
   WD Track my order
   ==========================================================================
   Renders every screen of the customer-facing order tracking page from the
   two JP Track endpoints (see .claude/API-contract.md). There are SIX
   `status` values on the wire; this file switches once on `status`, then
   maps over `shipments[]` and switches again per card on `pending` /
   `simplified` / `stage` — never on `status_code`, which is debugging-only.

   Everything the API returns is treated as data, never markup: every piece
   of customer/order/carrier text is written with textContent (via the `el`
   helper below), so nothing in an order — a name, an address, a carrier's
   scan description — can inject HTML.

   No polling, ever, and no refresh control either (per the client): one
   lookup per explicit form submit, full stop. The server enforces
   10 lookups/min and 30 failed/hour per IP on top. An `invalid` response
   is never cached client-side keyed by order number, and a failed lookup
   is never retried with a variation of the postal code — both would
   undermine the access control described in the contract.
   ========================================================================== */

(function () {
  'use strict';

  var CONFIG = window.themeVariables && window.themeVariables.trackMyOrder;
  if (!CONFIG) return;

  var root = document.getElementById('wd-track-order-app');
  if (!root) return;

  /* Shopify serves theme translations HTML-escaped (an apostrophe arrives as
     "&#39;"). Every render below uses textContent — the right call, nothing
     from the API or the locale files can ever run as markup — but textContent
     shows entities literally, so decode each string once at boot. DOMParser
     decodes without executing anything; any stray markup in a translation
     comes out as its text, not as elements. */
  function decodeEntities(value) {
    if (typeof value !== 'string' || value.indexOf('&') === -1) return value;
    return new DOMParser().parseFromString(value, 'text/html').documentElement.textContent;
  }

  function decodeStrings(node) {
    if (typeof node === 'string') return decodeEntities(node);
    if (Array.isArray(node)) return node.map(decodeStrings);
    if (node && typeof node === 'object') {
      var out = {};
      Object.keys(node).forEach(function (key) { out[key] = decodeStrings(node[key]); });
      return out;
    }
    return node;
  }

  var S = decodeStrings(CONFIG.strings);
  /* en-US, not en-CA, for the EN date formats: the prototype writes "Aug 3, 2026"
     and "6:35 PM", while en-CA's Intl data inserts periods ("Aug. 3", "p.m."). */
  var LOCALE = CONFIG.locale === 'fr' ? 'fr-CA' : 'en-US';
  var TZ_EASTERN = 'America/Toronto';

  /* ------------------------------------------------------------------------
     DOM helpers
     ------------------------------------------------------------------------ */

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (key) {
        var value = attrs[key];
        if (value === null || value === undefined || value === false) return;
        if (key === 'class') node.className = value;
        else if (key === 'text') node.textContent = value;
        else node.setAttribute(key, value === true ? '' : value);
      });
    }
    (children || []).forEach(function (child) {
      if (child === null || child === undefined) return;
      node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    });
    return node;
  }

  /* Static, hand-authored SVGs mirrored from snippets/icon.liquid so the
     JS-rendered screens can use the same marks without a Liquid round trip.
     The markup is a fixed string literal — never interpolated with any
     value from the API — so assigning it via innerHTML carries no risk. */
  var ICON_PATHS = {
    /* The REFERENCE pin (JPIcon.pin), not the theme icon set's.
       The theme version is an OPEN stroke that stops at (18.95, 10), leaving a
       gap in the upper right of the outline — at 16px that reads as an icon
       that failed to render. This one is a closed teardrop with its own
       circle, which is what the reference draws. */
    pin: '<svg viewBox="0 0 24 24" fill="none" focusable="false"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path><circle cx="12" cy="10" r="3" stroke="currentColor" stroke-width="1.6"></circle></svg>',
    phone: '<svg viewBox="0 0 24 24" fill="none" focusable="false"><path fill-rule="evenodd" clip-rule="evenodd" d="M20.6636 16.7325L17.6844 13.7366C17.2337 13.2827 16.4999 13.2827 16.048 13.7343L13.4005 16.3802L7.62246 10.6056L10.2734 7.95613C10.7241 7.5057 10.7253 6.77463 10.2746 6.32305L7.29311 3.33869C6.84126 2.8871 6.10976 2.8871 5.65791 3.33869L3.00462 5.98927L3 5.9858C3 14.2783 9.72568 21 18.023 21L20.6613 18.3633C21.1119 17.9129 21.1131 17.1841 20.6636 16.7325Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
    truck: '<svg viewBox="0 0 26 24" fill="none" focusable="false"><path d="M17 17H8.68004C8.68004 17 8 16 7 16M17 17V8M17 17H19.32M17 8V3H1V17H5.31996M17 8H23.5L25 12.5V17H22.68C22.68 17 22 16 21 16M21 16C22 16 23 17 23 18C23 19 22 20 21 20C20 20 19 19 19 18C19 17.6527 19.1206 17.3054 19.32 17M21 16C20.3473 16 19.6946 16.426 19.32 17M7 16C8 16 9 17 9 18C9 19 8 20 7 20C6 20 5 19 5 18C5 17.6527 5.12061 17.3054 5.31996 17M7 16C6.3473 16 5.69459 16.426 5.31996 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
    box: '<svg viewBox="0 0 24 24" fill="none" focusable="false"><path d="M12 21L21 17.1429V6.85714M12 21L3 17.1429V6.85714M12 21V10.7143M21 6.85714L12 3L3 6.85714M21 6.85714L12 10.7143M3 6.85714L12 10.7143" stroke="currentColor" stroke-width="1.5"></path></svg>',
    // The JP scottie, for imageless item rows — the prototype's placeholder tile.
    dog: '<svg viewBox="0 0 111.23 100" fill="currentColor" focusable="false"><path d="m32.71,52.85c4.42,2.51-10.91,27.58-11.9,39.72-.28,3.43,1.45,7.43,4.13,7.43,3.94,0,18.75,0,29.41,0,2.97,0,5.08-2.61,5.38-6.02.59-6.79-2.24-16.13-.96-16.95,1.91-1.24,10.46-1.22,12.12,0s.42,22.97,11.31,22.97c11.93,0,20.39,0,23.29,0,4.56,0,5.95-3.85,5.72-8.36-.48-9.01-8-25.41-9.26-36.15-.3-2.5.46-4.98,1.9-7.05,10.59-15.19-6.15-34.46-8.29-32.22s1.91,9.88-1.89,15.81c-.67,1.05-1.75,1.79-2.95,2.12-4.21,1.14-15.83,3.65-27.76.71-14.95-3.69-13.63-28.78-28.47-33.84-13.03-4.45-2.77,6.67-4.85,10.68-2.08,4.01-12.59,2.85-14.35,5.21-1.77,2.36.53,4.1-.56,5.57-.88,1.18-5.09,2.87-11.07,3.5-8.53,1.06-.72,28.64,9.74,35.49,6.5,4.26,15.19-10.98,19.34-8.62"></path></svg>',
    /* viewBox padded 0.6 on the left — the path starts at x=0 with a 1.2
       stroke, so half of it fell outside the box and the arrow tail was
       shaved by ~0.8px at render size. Geometry is unchanged; the box just
       gives the stroke room. */
    external: '<svg viewBox="-0.6 0 12.6 10" fill="none" focusable="false"><path d="M-3.63679e-07 5L9.75 5M9.75 5L5.85 9.16M9.75 5L5.85 0.840001" stroke="currentColor" stroke-width="1.2"></path></svg>',
    spinner: '<svg viewBox="25 25 50 50" focusable="false"><circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="4"></circle></svg>',
    formError: '<svg viewBox="0 0 18 26" focusable="false"><circle cx="9" cy="13" r="8" stroke="currentColor" stroke-width="1.5" fill="none"></circle><path d="M8.993 15.262a.972.972 0 00-.979.968c0 .539.44.99.98.99a.99.99 0 00.978-.99.972.972 0 00-.979-.968zm-.78-.649h1.561V8.706H8.212v5.907z" fill="currentColor"></path></svg>'
  };

  function icon(name, extraClass) {
    var span = el('span', { class: ('icon ' + (extraClass || '')).trim(), 'aria-hidden': 'true' });
    span.innerHTML = ICON_PATHS[name] || '';
    return span;
  }

  /* ------------------------------------------------------------------------
     Formatters — money is a string on the wire and is only ever formatted
     for display, never parsed and re-summed. Store hours are wall-clock
     strings and are never turned into a Date (formatClock does plain
     arithmetic on the "HH:MM" string instead). Every other timestamp is a
     real ISO instant and is safe to parse.
     ------------------------------------------------------------------------ */

  function interpolate(template, vars) {
    if (!template) return '';
    return Object.keys(vars || {}).reduce(function (str, key) {
      return str.split('@' + key + '@').join(vars[key] == null ? '' : vars[key]);
    }, template);
  }

  /* Always fr-CA ("238,00 $") — the store's money format. The prototype
     shows this exact format on the EN frames too, so it is per-store, not
     per-locale. */
  function formatMoney(amount, currency) {
    var n = Number(amount);
    if (!isFinite(n)) return amount;
    try {
      return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: currency }).format(n);
    } catch (e) {
      return amount;
    }
  }

  function parseDate(value, bareDate) {
    if (!value) return null;
    var d = new Date(bareDate ? value + 'T12:00:00Z' : value);
    return isNaN(d.getTime()) ? null : d;
  }

  /* "Friday, Aug 7" / "vendredi 7 août" — short month, per the prototype. */
  function formatLongDate(value, bareDate) {
    var d = parseDate(value, bareDate);
    if (!d) return '';
    return new Intl.DateTimeFormat(LOCALE, { weekday: 'long', month: 'short', day: 'numeric' }).format(d);
  }

  function formatShortDate(value) {
    var d = parseDate(value, false);
    if (!d) return '';
    return new Intl.DateTimeFormat(LOCALE, { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
  }

  /* "Aug 7" / "7 août" — the halves of the estimated-delivery range. */
  function formatMonthDay(value) {
    var d = parseDate(value, true);
    if (!d) return '';
    return new Intl.DateTimeFormat(LOCALE, { month: 'short', day: 'numeric' }).format(d);
  }

  /* The anchor's date line. A lone date renders like the prototype's screen 3
     ("Friday, Aug 7"); a service-estimate range like its screen 4 — EN
     "Aug 7 to Aug 8", FR "7 au 8 août" (same-month FR elides the first month,
     which is why the FR start half is day-only). */
  function formatAnchorDate(shipment) {
    var start = shipment.estimated_delivery_date;
    var end = shipment.estimated_delivery_end_date;
    if (!end || end === start) return formatLongDate(start, true);
    var from = formatMonthDay(start);
    if (CONFIG.locale === 'fr') {
      var a = parseDate(start, true);
      var b = parseDate(end, true);
      if (a && b && a.getUTCMonth() === b.getUTCMonth()) {
        from = String(a.getUTCDate());
      }
    }
    return interpolate(S.shipped.anchorRange, { from: from, to: formatMonthDay(end) });
  }

  /* "Today, 7:42 AM" / "Yesterday, 11:10 PM" / "Aug 4, 6:35 PM" — the prototype's
     timeline shows today's and yesterday's scans relatively, judged in the
     store's timezone (Eastern), same as the times themselves. */
  function easternDayStamp(date) {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: TZ_EASTERN, year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(date);
  }

  function formatEventTime(value) {
    var d = parseDate(value, false);
    if (!d) return '';
    var time = new Intl.DateTimeFormat(LOCALE, {
      timeZone: TZ_EASTERN, hour: 'numeric', minute: '2-digit'
    }).format(d);
    var stamp = easternDayStamp(d);
    var now = new Date();
    if (stamp === easternDayStamp(now)) {
      return S.shared.today + ', ' + time;
    }
    if (stamp === easternDayStamp(new Date(now.getTime() - 86400000))) {
      return S.shared.yesterday + ', ' + time;
    }
    var day = new Intl.DateTimeFormat(LOCALE, {
      timeZone: TZ_EASTERN, month: 'short', day: 'numeric'
    }).format(d);
    return day + ', ' + time;
  }

  function formatUpdatedAgo(value) {
    var d = parseDate(value, false);
    if (!d) return '';
    var minutes = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    return minutes < 1 ? S.shared.updatedJustNow : interpolate(S.shared.updatedAgo, { minutes: String(minutes) });
  }

  /* "10:00" -> "10 AM" / "10 h"; "19:00" -> "7 PM" / "19 h". Pure string
     arithmetic on purpose — these are local wall-clock strings, not
     timestamps, and constructing a Date from them is exactly what the
     contract warns against (it would silently apply the visitor's own
     timezone/DST instead of the store's). */
  function formatClock(hhmm) {
    var parts = hhmm.split(':');
    var hour = parseInt(parts[0], 10);
    var minute = parts[1];
    if (CONFIG.locale === 'fr') {
      return minute === '00' ? (hour + ' h') : (hour + ' h ' + minute);
    }
    var period = hour >= 12 ? 'PM' : 'AM';
    var hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12;
    return minute === '00' ? (hour12 + ' ' + period) : (hour12 + ':' + minute + ' ' + period);
  }

  /* "h7t1c7" -> "H7T 1C7". Uppercased, everything that is not a letter or a
     digit dropped, a space put back after the third character. Runs on every
     keystroke so nobody has to guess the format (V2 lookup note 3). Capped at
     six characters, which is the whole of a Canadian postal code — a US ZIP
     just passes through as digits and never reaches the space rule. */
  function formatPostal(raw) {
    var s = String(raw).toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
    return s.length > 3 ? s.slice(0, 3) + ' ' + s.slice(3) : s;
  }

  function digitsOnly(raw) {
    return String(raw).replace(/\D/g, '');
  }

  /* The full instant a parcel was delivered, for the "Delivered <date> at
     <time>" lead and the 48-hour claim gate.

     actual_delivery_date is a BARE DATE and carries no time, so the scan's own
     timestamp is preferred when the carrier sent one — that is the whole of
     the design's "the time only when the carrier sends one" rule. events[0] is
     the current scan; it only counts here when it is the delivery scan. */
  function deliveryMoment(shipment) {
    var events = shipment.events || [];
    var current = events[0];

    if (current && current.stage === 'delivered' && current.occurred_at) {
      var withTime = parseDate(current.occurred_at, false);
      if (withTime) return { date: withTime, hasTime: true };
    }

    var bare = parseDate(shipment.actual_delivery_date, true);
    return bare ? { date: bare, hasTime: false } : null;
  }

  function formatTimeOfDay(date) {
    return new Intl.DateTimeFormat(LOCALE, {
      timeZone: TZ_EASTERN, hour: 'numeric', minute: '2-digit'
    }).format(date);
  }

  /* `labels` is optional: the tracking-number button keeps the shared
     Copy/Copied pair, while the share button names what it copied. Both swap
     textContent, so neither may carry an icon. */
  function copyToClipboard(value, button, labels) {
    var original = (labels && labels.idle) || S.shared.copyLabel;
    var confirmed = (labels && labels.done) || S.shared.copied;
    function done() {
      button.textContent = confirmed;
      setTimeout(function () { button.textContent = original; }, 1600);
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(value).then(done, done);
      return;
    }
    var temp = el('textarea', { readonly: true, style: 'position:absolute;left:-9999px' });
    temp.value = value;
    document.body.appendChild(temp);
    temp.select();
    try { document.execCommand('copy'); } catch (e) { /* clipboard unavailable in this browser */ }
    document.body.removeChild(temp);
    done();
  }

  /* ------------------------------------------------------------------------
     Shared building blocks
     ------------------------------------------------------------------------ */

  function metaItem(label, value) {
    return el('div', { class: 'wd-order-meta__item' }, [
      el('dt', { text: label }),
      el('dd', { text: value })
    ]);
  }

  function buildOrderMeta(order, extra) {
    // Bare digits ("106482"), not order.name ("#106482") — the prototype's
    // meta rail shows the number without the hash.
    var items = [
      metaItem(S.shared.orderNo, order.number ? String(order.number) : (order.name || ''))
    ];
    // Every row below is dropped rather than rendered with an empty value —
    // a labelled blank in the meta rail reads as a loading bug.
    var placed = formatShortDate(order.placed_at);
    if (placed) items.push(metaItem(S.shared.placed, placed));
    if (extra && extra.value) items.push(metaItem(extra.label, extra.value));
    // `total` is documented as nullable — an order the plugin could not price
    // returns null rather than "". Dereferencing it threw, and because the
    // whole render happens inside the lookup's promise chain the throw
    // surfaced to the customer as "something went wrong" on an order that was
    // perfectly fine. Drop the row instead.
    if (order.total) items.push(metaItem(S.shared.total, formatMoney(order.total.amount, order.total.currency)));
    return el('dl', { class: 'wd-order-meta' }, items);
  }

  /* "← Track another order" — sits above the head on EVERY result screen and
     returns to an empty lookup form (V2 processing note 1). A button, not a
     link: there is no URL for a result screen to go back from, because the
     whole flow lives in one page load. */
  function buildBackLink() {
    var button = el('button', { type: 'button', class: 'wd-track-order__back' }, [
      el('span', { class: 'wd-track-order__back-arrow', 'aria-hidden': 'true', text: '←' }),
      document.createTextNode(S.shared.back)
    ]);
    /* When THIS page view pushed the entry, step back through it instead of
       re-rendering over the top: otherwise the arrow and the browser's own
       button disagree about where the customer is, and Back would then need
       two presses to leave a form already on screen. The popstate handler does
       the rendering.

       ⚠️ `pushedByThisView`, NOT `onResultEntry`. A history entry outlives the
       page that made it, so a customer returning from the contact page without
       a bfcache hit arrives on our own marker with a freshly booted script.
       Stepping back THERE walks off the tracking page entirely — the arrow
       would take them somewhere they never asked to go. Draw the form instead
       and correct the entry. */
    button.addEventListener('click', function () {
      if (pushedByThisView()) { window.history.back(); return; }
      clearResultMarker();
      renderLookup();
    });
    return button;
  }

  function buildHead(opts) {
    var head = el('div', { class: 'wd-track-order__head' });
    if (opts.back) head.appendChild(buildBackLink());
    var row = el('div', { class: 'wd-track-order__badge-row' });
    if (opts.badgeText) {
      row.appendChild(el('span', {
        class: 'wd-status-badge wd-status-badge--' + (opts.tone || 'oxide'),
        text: opts.badgeText
      }));
    } else {
      row.appendChild(el('span', { class: 'wd-label wd-track-order__eyebrow', text: S.shared.eyebrow }));
    }
    head.appendChild(row);
    head.appendChild(el('h1', { class: 'heading h1', id: 'wd-track-order-heading', tabindex: '-1', text: opts.title }));
    if (opts.lead) head.appendChild(el('p', { class: 'wd-track-order__lead', text: opts.lead }));

    if (opts.meta) {
      head.appendChild(opts.meta);

      /* Keyed off `meta` rather than a flag of its own, because `meta` is
         exactly what the six order screens pass and the invalid, rate-limited
         and error screens do not. There is nothing to share from a screen
         that never resolved an order. */
      var share = buildShareRow();
      if (share) head.appendChild(share);
    }

    return head;
  }

  function buildTwoColumnLayout(mainNodes, asideNodes) {
    var main = el('div', { class: 'wd-track-order__main' });
    var aside = el('div', { class: 'wd-track-order__aside' });
    // Null-tolerant, like the `el` helper: a builder that has nothing to show
    // (an order with no shipping address) returns null rather than an empty
    // card, and appending that would throw mid-render.
    mainNodes.forEach(function (n) { if (n) main.appendChild(n); });
    asideNodes.forEach(function (n) { if (n) aside.appendChild(n); });
    return el('div', { class: 'wd-track-order__layout' }, [
      el('div', { class: 'wd-track-order__grid' }, [main, aside])
    ]);
  }

  /* "2 Items" / "1 Item" — the trailing-s strip covers both live locales
     (EN "Items"→"Item", FR "Articles"→"Article"); the other locale files
     fall back to English anyway. */
  function itemsLabel(qty) {
    return qty + ' ' + (qty === 1 ? S.shared.items.replace(/s$/i, '') : S.shared.items);
  }

  /* `compact: true` titles the list "2 Items" (a parcel's own items, inside a
     shipment card) instead of "2 items in this order" (the whole order, in
     the aside) — matching the prototype's two different list headings.

     Counts are LINE counts, not unit sums: the prototype titles the qty-2
     tank parcel "1 item", and the 4-unit order "3 items in this order". */
  /* `refunded: true` strikes the price through, fades the thumbnail and adds
     the REFUNDED tag (V2 cancelled note 3 — "the same tag works in any item
     list", which is why it lives here and not in a screen). */
  function buildItemRow(item, refunded) {
    var media = item.image
      ? el('img', { class: 'wd-item-list__image', src: item.image, alt: '', loading: 'lazy' })
      : el('div', { class: 'wd-item-list__placeholder' }, [icon('dog')]);
    // Prototype row: name over "variant · Qty n", price pinned to the end.
    var qtyText = interpolate(S.shared.qty, { count: String(item.quantity) });
    var info = [
      el('p', { class: 'wd-item-list__title', text: item.title }),
      el('p', { class: 'wd-item-list__variant', text: item.variant ? (item.variant + ' · ' + qtyText) : qtyText })
    ];
    /* FINAL SALE sits under the "colour / size · Qty n" line, per the client's
       markup. REFUNDED wins when a line is both: the money coming back is the
       more urgent fact, and stacking two tags on one row reads as noise. */
    /* The `&& S.…` guards are not belt-and-braces, they are load-bearing.
       el() SKIPS a text value of undefined rather than printing "undefined",
       so a tag whose string is missing renders as a correctly-styled EMPTY
       chip — a bordered pink rectangle with nothing in it. That is exactly
       what a half-deployed theme looks like: assets/*.js and *.css are
       separate files from sections/*.liquid, and pushing one without the
       other leaves the renderer asking for a string the page never defined.
       No string, no chip. */
    if (refunded && S.refund.itemBadge) {
      info.push(el('span', { class: 'wd-item-list__tag', text: S.refund.itemBadge }));
    } else if (!refunded && item.final_sale && S.shared.finalSale) {
      info.push(el('span', { class: 'wd-item-list__tag wd-item-list__tag--sale', text: S.shared.finalSale }));
    }

    var row = [media, el('div', { class: 'wd-item-list__info' }, info)];
    if (item.price) row.push(buildItemPrice(item, refunded));

    return el('div', { class: 'wd-item-list__row' + (refunded ? ' is-refunded' : '') }, row);
  }

  /* The price cell: the amount paid, preceded by the struck-through
     compare-at price when the order carries one.

     `compare_at_price` is OPTIONAL and today the plugin never sends it —
     reading it needs the read_products scope, which the Admin token does not
     have (see the ORDER_FIELDS note in class-jpt-shopify.php). Written to
     tolerate that: no field, or one that is not actually higher than the price
     paid, renders exactly the single-price row that shipped before. Add the
     scope and the strike appears with no change here.

     Guarded on `>` rather than mere presence because Shopify keeps a stale
     compare-at on a variant after a sale ends, and "$39 was $39" is worse than
     no strike at all.

     ⚠️ WHEN WIRING THE BACKEND HALF: `compare_at_price` must be a LINE figure,
     already multiplied by quantity, because `price` is one. Shopify's
     compare-at is per unit, so handing it straight through would print
     "was $39, now $78" on a qty-2 line — a sale that reads as a surcharge. */
  function buildItemPrice(item, refunded) {
    var paid = formatMoney(item.price.amount, item.price.currency);
    var was = item.compare_at_price;
    var show = !refunded
      && was
      && parseFloat(was.amount) > parseFloat(item.price.amount);

    if (!show) return el('p', { class: 'wd-item-list__price', text: paid });

    /* <s>, not a styled <span>: "no longer accurate" is exactly what the
       element means, so the old price survives with its meaning intact when
       the CSS does not load and is not left as a bare number a screen reader
       would read out as a second, equally valid price. */
    return el('p', { class: 'wd-item-list__price' }, [
      el('s', { class: 'wd-item-list__was', text: formatMoney(was.amount, was.currency) }),
      el('span', { class: 'wd-item-list__now', text: paid })
    ]);
  }

  function buildItemList(items, compact) {
    var lineCount = items.length;
    // "0 items in this order" is never useful. Callers all tolerate null.
    if (!lineCount) return null;
    /* Singular has its own string rather than the trailing-s strip itemsLabel
       uses: the "s" is mid-sentence here ("1 item in this order"), so there is
       nothing on the end to take off. */
    var title = compact
      ? itemsLabel(lineCount)
      : interpolate(lineCount === 1 ? S.shared.itemsInOne : S.shared.itemsIn, { count: String(lineCount) });
    var card = el('div', { class: 'wd-card' }, [
      el('p', { class: 'wd-card__title', text: title })
    ]);
    items.forEach(function (item) {
      card.appendChild(buildItemRow(item, false));
    });
    return card;
  }

  /* The refunded piece(s), shown UNDER the items that are still coming rather
     than mixed into them — V2 shipped note: "the items still coming are in the
     usual list, and the refunded piece gets its own block under it". A partial
     refund never takes over the screen; the order is still whatever it was. */
  function buildRefundedBlock(items, opts) {
    opts = opts || {};
    var card = el('div', { class: 'wd-card wd-card--refund' }, [
      el('p', { class: 'wd-card__title', text: opts.title || S.refund.partialTitle })
    ]);
    items.forEach(function (item) {
      card.appendChild(buildItemRow(item, true));
    });
    if (opts.note) card.appendChild(el('p', { class: 'wd-card__note wd-card__note--pad', text: opts.note }));
    return card;
  }

  /* Singular vs plural copy for the partial-refund block. One refunded line
     reads "One piece won't be coming / we couldn't fulfill this one"; two or
     more switch to the plural strings, so the heading and note match the number
     of pieces actually shown in the block. */
  function partialRefundCopy(count) {
    return (count > 1)
      ? { title: S.refund.partialTitlePlural, note: S.refund.partialNotePlural }
      : { title: S.refund.partialTitle, note: S.refund.partialNote };
  }

  /* "If you were charged" — deliberately does NOT assert that a charge was
     made (V2 cancelled note 2). Shopify tells us an order was cancelled, not
     whether the customer's card settled or only held, so the copy covers both
     and the card never promises a refund that was never taken. */
  function buildRefundCard(money) {
    var rows = [];
    if (money) {
      rows.push(el('div', {}, [
        el('dt', { text: S.refund.amount }),
        el('dd', { class: 'wd-refund__amount', text: formatMoney(money.amount, money.currency) })
      ]));
    }
    rows.push(el('div', {}, [
      el('dt', { text: S.refund.method }),
      el('dd', { text: S.refund.methodValue })
    ]));
    rows.push(el('div', {}, [
      el('dt', { text: S.refund.timing }),
      el('dd', { text: S.refund.timingValue })
    ]));

    return el('div', { class: 'wd-card' }, [
      el('p', { class: 'wd-card__title', text: S.refund.cardTitle }),
      el('dl', { class: 'wd-refund' }, rows),
      el('p', { class: 'wd-card__note wd-card__note--pad', text: S.refund.note })
    ]);
  }

  /* Prototype: text block left, CTA block right (stacked on mobile), with
     the hours/email line under the outline button. */
  function buildHelpCard() {
    return el('div', { class: 'wd-help-card' }, [
      el('div', { class: 'wd-help-card__text' }, [
        el('p', { class: 'wd-help-card__title', text: S.shared.helpTitle }),
        el('p', { class: 'wd-help-card__body', text: S.shared.helpBody })
      ]),
      el('div', { class: 'wd-help-card__cta' }, [
        el('a', { class: 'button button--secondary', href: CONFIG.contactUrl, text: S.shared.helpCta }),
        el('p', { class: 'wd-help-card__alt', text: S.shared.helpAlt })
      ])
    ]);
  }

  /* The store-page link arrives from the API as a bare "/pages/<handle>", with
     no locale on it, so the French page was linking a French shopper to the
     English boutique page. Prefix it with the locale root the section handed
     us — empty on the primary locale, "/fr" on the French one.

     Handles whose FRENCH page is a translated handle still resolve: Shopify
     redirects /fr/pages/<en-handle> to the translated one. The contact link
     avoids even that hop because Liquid can look its page up by handle; there
     is no equivalent here, because this handle only exists at runtime. */
  function localizePath(path) {
    if (!path || typeof path !== 'string') return path;

    var root = CONFIG.rootUrl || '';

    // Absolute URLs, protocol-relative URLs and anything already carrying the
    // locale root are left exactly as they are.
    if (root === '' || /^[a-z]+:|^\/\//i.test(path) || path.charAt(0) !== '/') return path;
    if (path === root || path.indexOf(root + '/') === 0) return path;

    return root + path;
  }

  /* The city line, built only from the parts that are actually there.
     Every one of city / province / postal_code is nullable, and concatenating
     them blind printed the literal string "null, null null" to the customer. */
  function addressCityLine(address) {
    var town = [address.city, address.province].filter(Boolean).join(', ');
    return [town, address.postal_code].filter(Boolean).join(' ');
  }

  function addressLines(address) {
    if (!address) return [];
    return [address.name, address.line1, address.line2, addressCityLine(address)].filter(Boolean);
  }

  /* Where the claim FORM lives.

     Section setting first, translated string second. The setting is one URL
     for every language: "Email us" and "Envoyez-nous un courriel" are the same
     page with a translated handle, so Liquid resolves the French URL from the
     English one and no second field is needed. The translated string is the
     fallback for a blank setting. Neither is hardcoded here.

     Only a same-origin absolute path is accepted, from either source. A full
     URL, a protocol-relative one, or anything carrying a scheme is refused and
     the mailto: route takes over: a button that hands over a customer's order
     details must not be re-pointable off-site by a typo in a settings field.

     localizePath() then adds the "/fr" root, and skips it when the value
     already carries one. */
  function claimPagePath() {
    var m = (S.delivered && S.delivered.miss) || {};

    for (var i = 0, candidates = [CONFIG.claimPageUrl, m.claimPage]; i < candidates.length; i++) {
      var value = typeof candidates[i] === 'string' ? candidates[i].trim() : '';

      // "/pages/x" yes; "//evil.com" and "https://…" no.
      if (/^\/[^\/]/.test(value)) return localizePath(value);
    }

    return null;
  }

  /* The key the claim form reads its prefill out of. Also in
     snippets/wd-claim-prefill.liquid — change both together. */
  var CLAIM_PREFILL_KEY = 'wd-claim-prefill';

  /* How long the opener leaves the handoff sitting there before wiping it.
     The claim form is opened in a NEW TAB, so this page stays alive and can
     clean up after itself — which is the whole reason the window can be this
     short. Long enough that a customer who gets distracted between the click
     and the new tab still finds the form filled in. */
  var CLAIM_PREFILL_CLEANUP_MS = 10 * 60 * 1000;

  /* Hand the claim details to the contact form on the next page.

     ⚠️ THE SHIPPING ADDRESS IS NOT IN HERE, ON PURPOSE. The API contract is
     explicit: the address must not go in a URL, an analytics event,
     localStorage, or anything that outlives the render — the whole page is
     guarded by only an order number plus a postal code, so the address must
     not become easier to reach. The mailto: route could carry it because a
     mailto: never leaves the click handler; a form on another page cannot.
     Nothing is lost: the team reads the address off the order in Shopify from
     the order number, which IS in here and is the field the form asks for.

     WHY localStorage AND NOT sessionStorage, given the contract names
     localStorage by name: the claim form opens in a NEW TAB, and sessionStorage
     is per-tab. Browsers are supposed to hand a new tab a copy of the opener's
     session storage, but whether that survives `rel="noopener"` differs between
     engines and Safari has not been reliable about it. A customer arriving at a
     blank form is the exact friction this was built to remove, so the handoff
     cannot rest on it. localStorage is shared across tabs everywhere.

     A query string was the other option and is worse: it would put the
     customer's e-mail, phone and order number in browser history, the referrer
     header and the server access log — permanently, and outside our reach.

     THE ADDRESS IS STILL NOT IN HERE, which is what the contract's localStorage
     rule is actually protecting. What is here is bounded on three sides:
       - the reader deletes the key before it even parses it;
       - every payload is stamped, and a stale one is discarded unread;
       - this page wipes the key itself after CLAIM_PREFILL_CLEANUP_MS, so a
         customer who clicks and never opens the tab does not leave their
         details sitting in storage. The page is still open to do it precisely
         because the form opened in a new tab. */
  function stashClaimPrefill(facts, contact) {
    var who = contact && typeof contact === 'object' ? contact : {};

    try {
      window.localStorage.setItem(CLAIM_PREFILL_KEY, JSON.stringify({
        at: Date.now(),
        order: facts.number,
        message: interpolate(S.delivered.miss.formMessage, facts),

        /* Every one of these is nullable on the API and the reader skips
           blanks, so a guest order with no phone simply leaves that field for
           the customer. First and last name come from Shopify already split —
           we never guess where a name divides. */
        firstName: who.first_name || '',
        lastName: who.last_name || '',
        email: who.email || '',
        phone: who.phone || ''
      }));

      // Clean up after ourselves. The reader normally gets there first and
      // removes it; this is for the customer who clicks and never looks at
      // the tab, so their details do not outlive the visit.
      window.setTimeout(function () {
        try { window.localStorage.removeItem(CLAIM_PREFILL_KEY); } catch (e) {}
      }, CLAIM_PREFILL_CLEANUP_MS);
    } catch (e) {
      // Storage disabled or full. The link still opens; the customer fills
      // the form in by hand. Never block the claim over a prefill.
    }
  }

  /* "Marked delivered, but nothing there?" — collapsed by default and sitting
     UNDER the tracking history, so the screen reads as good news first and does
     not suggest something went wrong (V2 delivered note 2).

     The report button is gated for 48 hours after the delivery scan (note 3):
     most parcels scanned early turn up inside that window, and a claim opened
     before it is one the carrier will refuse anyway. The locked state names the
     date it opens rather than just being dead.

     ⚠️ The claim email carries the shipping address, and the API contract is
     explicit that the address must not be put anywhere that outlives the
     render — not a URL, not a data- attribute. So the mailto: is assembled in
     the click handler and never exists as an href in the DOM. */
  function buildMissingBlock(order, shipment, moment) {
    var m = S.delivered.miss;
    var opensAt = new Date(moment.date.getTime() + 48 * 3600 * 1000);
    var isOpen = Date.now() >= opensAt.getTime();

    var details = el('details', { class: 'wd-missing' });
    details.appendChild(el('summary', { class: 'wd-missing__summary' }, [
      el('span', { text: m.title }),
      el('span', { class: 'wd-missing__chevron', 'aria-hidden': 'true' })
    ]));

    var tips = el('ul', { class: 'wd-bring-list' });
    (m.tips || []).forEach(function (tip) { tips.appendChild(el('li', { text: tip })); });

    var action;
    var claimPage = claimPagePath();

    /* Facts about the parcel, shared by both routes below. Deliberately
       WITHOUT the shipping address — see stashClaimPrefill(). */
    function claimFacts() {
      return {
        number: order.number || '',
        scan: formatShortDate(moment.date.toISOString()) +
          (moment.hasTime ? ' · ' + formatTimeOfDay(moment.date) : ''),
        carrier: shipment.carrier || '',
        tracking: shipment.tracking_number || '',
        value: order.goods_total ? formatMoney(order.goods_total.amount, order.goods_total.currency) : ''
      };
    }

    if (isOpen && claimPage) {
      /* Opens in a new tab, so the customer keeps the tracking page they were
         reading — the claim form asks about the delivery scan and the tracking
         number, and sending them away from the screen showing both would mean
         going back for it.

         rel="noopener noreferrer": the claim page has no business reaching
         back into this one through window.opener, and the referrer would carry
         this page's URL. Neither is needed — the handoff goes through storage,
         not through the window relationship — so both are cut.

         `target` is also why the handoff is localStorage rather than
         sessionStorage; see stashClaimPrefill(). */
      action = el('a', {
        class: 'button button--primary wd-missing__cta',
        href: claimPage,
        target: '_blank',
        rel: 'noopener noreferrer',
        text: m.cta
      });
      action.addEventListener('click', function () {
        // Not preventDefault()'d — the handoff is written synchronously and
        // the browser then opens the tab, so it is always in place before the
        // form loads. If storage throws (private mode, storage disabled) the
        // tab still opens and the customer fills the form in by hand.
        stashClaimPrefill(claimFacts(), order.contact);
      });
    } else if (isOpen) {
      // No claim page configured — a mis-typed setting must not leave a dead
      // button that swallows the claim. The contact page always exists, so it
      // is the floor. The customer types their own details there; nothing is
      // prefilled, because nothing knows which form it is.
      action = el('a', {
        class: 'button button--primary wd-missing__cta',
        href: CONFIG.contactUrl, text: m.cta
      });
    } else {
      action = el('span', {
        class: 'button button--primary wd-missing__cta is-locked',
        'aria-disabled': 'true', text: m.cta
      });
    }

    details.appendChild(el('div', { class: 'wd-missing__inner' }, [
      el('p', { class: 'wd-missing__body', text: m.body }),
      tips,
      el('div', { class: 'wd-missing__action' }, [
        action,
        el('p', { class: 'wd-missing__note', text: isOpen ? m.readyNote : interpolate(m.waitNote, { date: formatLongDate(opensAt.toISOString(), false) }) })
      ])
    ]));

    return details;
  }

  /* Returns null when there is no address to show — an order with no shipping
     address (the plugin hands back a block of nulls for one) would otherwise
     render an empty "Shipping to" card. Callers drop a null node. */
  function buildShipToCard(address) {
    var lines = addressLines(address);
    if (!lines.length) return null;

    var addr = el('address', { style: 'font-style:normal' });
    lines.forEach(function (line, i) {
      if (i > 0) addr.appendChild(el('br'));
      addr.appendChild(document.createTextNode(line));
    });
    return el('div', { class: 'wd-card' }, [
      el('p', { class: 'wd-card__title', text: S.shared.shippingTo }),
      addr
    ]);
  }

  /* note: the carrier's service level ("Purolator Ground") under the date,
     when the API provides one — every anchor in the prototype carries it. */
  function buildAnchor(dateText, label, note) {
    var nodes = [
      el('span', { class: 'wd-label wd-anchor__label', text: label }),
      el('p', { class: 'wd-anchor__date', text: dateText })
    ];
    if (note) nodes.push(el('p', { class: 'wd-anchor__note', text: note }));
    return el('div', { class: 'wd-anchor' }, nodes);
  }

  /* No refresh control anywhere, per the design: the page renders one
     lookup and stays put (the API's rate limits forbid auto-updating, and
     the client asked for no manual control either — a shopper who wants
     fresher data re-submits the form). The "updated N min ago" fragment
     inside the note text is the prototype's own wording. */
  function buildSourceNote(text) {
    return el('p', { class: 'wd-source-note', text: text });
  }

  /* One card: carrier row, tracking row (number left, COPY right, soft rule
     above), an optional explainer note, then the carrier CTA. On the
     timeline screens the CTA is the ink outline; on the no-timeline /
     other-carrier screens it is the primary pink (opts.ctaPrimary). */
  function buildShipmentFacts(shipment, opts) {
    var rows = [];

    // No carrier row at all on a hand-fulfilled parcel — a "CARRIER" label
    // over a blank line looks like data that failed to load.
    if (shipment.carrier) {
      rows.push(el('div', { class: 'wd-shipment-facts__carrier' }, [
        el('span', { class: 'wd-shipment-facts__icon' }, [icon('truck')]),
        el('div', {}, [
          el('span', { class: 'wd-label', text: S.shipped.carrier }),
          el('p', { class: 'wd-shipment-facts__name', text: shipment.carrier })
        ])
      ]));
    }

    if (shipment.tracking_number) {
      var copyBtn = el('button', { type: 'button', class: 'wd-copy-button', text: S.shared.copyLabel });
      copyBtn.addEventListener('click', function () { copyToClipboard(shipment.tracking_number, copyBtn); });

      /* V2 shipped note 1: the number itself is now a link to the carrier's
         page, with the copy button kept beside it for pasting elsewhere. Falls
         back to plain text when the API has no URL for this carrier — the
         number is still the useful thing, and a dead link is worse than none. */
      var trackable = shipment.tracking_url && /^https?:\/\//i.test(shipment.tracking_url);
      var numberNode = trackable
        ? el('a', {
            class: 'wd-shipment-facts__link', href: shipment.tracking_url,
            target: '_blank', rel: 'noopener noreferrer'
          }, [
            document.createTextNode(shipment.tracking_number + ' '),
            icon('external', 'wd-external-icon')
          ])
        : document.createTextNode(shipment.tracking_number);

      rows.push(el('div', { class: 'wd-shipment-facts__tracking' }, [
        el('div', {}, [
          el('span', { class: 'wd-label', text: S.shipped.trackingNo }),
          el('p', { class: 'wd-shipment-facts__no' }, [numberNode])
        ]),
        copyBtn
      ]));
    }

    if (opts.note) rows.push(el('p', { class: 'wd-card__note', text: opts.note }));

    // tracking_url is documented as always http/https and pre-validated server-side,
    // but the contract still asks for it to be escaped in the template — this is
    // that belt-and-braces check.
    if (shipment.tracking_url && /^https?:\/\//i.test(shipment.tracking_url)) {
      rows.push(el('a', {
        class: 'button ' + (opts.ctaPrimary ? 'button--primary' : 'button--secondary'),
        href: shipment.tracking_url, target: '_blank', rel: 'noopener noreferrer'
      }, [
        document.createTextNode(opts.ctaLabel + ' '),
        icon('external', 'wd-external-icon')
      ]));
    }

    // Nothing to state — no carrier, no tracking number, no explainer, no
    // link. An empty bordered card is worse than no card.
    if (!rows.length) return null;

    return el('div', { class: 'wd-card wd-shipment-facts' }, rows);
  }

  /* ⚠️ LANGUAGE OF THE TIMELINE.
     `events[].description` is the carrier's own wording and the API contract is
     explicit that it ALWAYS ARRIVES IN ENGLISH — there is no French feed to
     switch to (contract § Known gaps 2).

       EN — print the carrier's words verbatim, as the design's timeline does.
       FR — translate the carrier's phrase through the table below, which keeps
            the DETAIL ("Arrived at delivery facility" stays "Arrivée au centre
            de distribution", it does not flatten to "En transit"). Only a
            phrase we cannot map confidently falls back to the broader
            status_code/stage wording, and the raw English is never printed.

     Patterns run against the description lowercased with punctuation stripped,
     MOST SPECIFIC FIRST — the order is load-bearing. Two examples of why:
     "Arrived at delivery facility" has to be tested before the generic
     /deliver/ or it reads as "Delivered", and every negative ("not delivered",
     "delivery attempted") has to be tested before it too. */
  var SCAN_PATTERNS = [
    [/return(ed|ing)?\s+to\s+(sender|shipper)|undeliverable/, 'returned'],
    [/refus/, 'refused'],
    [/damag/, 'damaged'],
    [/\b(not|un)\s*deliver|deliver\w*\s+(not|un|fail)/, 'notDelivered'],
    [/attempt|missed\s+you|no\s+one\s+(available|home)/, 'attempted'],
    [/(available|ready)\s+for\s+pick\s*up|held\s+(at|for)|pick\s*up\s+(point|location)|awaiting\s+collection|collection\s+point/, 'heldPickup'],
    [/customs|clearance/, 'customs'],
    [/address/, 'addressIssue'],
    [/delay/, 'delayed'],
    [/out\s+for\s+delivery|on\s+vehicle\s+for\s+delivery|with\s+(the\s+)?(delivery\s+)?courier|loaded\s+for\s+delivery/, 'outForDelivery'],
    [/arriv\w*\s+at\s+(the\s+)?\w*\s*(delivery|destination|local)/, 'arrivedDeliveryFacility'],
    [/depart|left\s+(the\s+)?\w*\s*facility|outbound/, 'departedFacility'],
    [/arriv|received\s+at|inbound|scanned\s+at/, 'receivedFacility'],
    [/sort|process\w*\s+at/, 'sorting'],
    [/in\s+transit|on\s+(its|the)\s+way|en\s?route/, 'inTransit'],
    [/pick\w*\s*up|collected|received\s+by\s+(the\s+)?carrier|shipment\s+(picked|received)/, 'pickedUp'],
    [/label|shipment\s+information|billing\s+information|order\s+processed|manifest|pre\s*-?\s*advice/, 'labelCreated'],
    [/deliver/, 'delivered'],
    [/exception|problem|issue|held/, 'exception']
  ];

  function scanPhrase(description) {
    if (!description || !S.scan) return null;

    var norm = String(description).toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!norm) return null;

    for (var i = 0; i < SCAN_PATTERNS.length; i++) {
      if (SCAN_PATTERNS[i][0].test(norm)) {
        var phrase = S.scan[SCAN_PATTERNS[i][1]];
        // A theme whose section and locale files land out of step — which this
        // one has done on deploy — renders "Translation missing: fr.…" as the
        // value. Printing that into the customer's timeline is worse than the
        // broader status, so treat it as absent and let the caller fall back.
        if (!phrase || /translation missing/i.test(phrase)) return null;
        return phrase;
      }
    }

    return null;
  }

  function describeEvent(ev, carrier) {
    var byCode = {
      NY: interpolate(S.events.packing, { carrier: carrier }),
      AC: interpolate(S.events.shipped, { carrier: carrier }),
      IT: S.events.inTransit,
      AT: interpolate(S.events.attempted, { carrier: carrier }),
      DE: S.events.delivered,
      SP: interpolate(S.events.awaitingCollection, { carrier: carrier }),
      EX: interpolate(S.events.exception, { carrier: carrier })
    };

    var mapped = (ev.status_code && byCode[ev.status_code]) || null;

    if (!mapped) {
      switch (ev.stage) {
        case 'packing': mapped = interpolate(S.events.packing, { carrier: carrier }); break;
        case 'shipped': mapped = interpolate(S.events.shipped, { carrier: carrier }); break;
        case 'in-transit': mapped = S.events.inTransit; break;
        case 'delivered': mapped = S.events.delivered; break;
        case 'exception': mapped = interpolate(S.events.exception, { carrier: carrier }); break;
      }
    }

    if (CONFIG.locale === 'fr') {
      // Translated phrase, then the broader status, then a last-resort line.
      // ev.description is deliberately NOT in this chain: it is English, and
      // reaching for it here is exactly the leak this function exists to stop.
      return scanPhrase(ev.description) || mapped || interpolate(S.events.unknown, { carrier: carrier });
    }

    return ev.description || mapped || interpolate(S.events.unknown, { carrier: carrier });
  }

  function buildTimeline(events, carrier) {
    var list = el('ol', { class: 'wd-timeline' });
    events.forEach(function (ev, index) {
      var metaParts = [];
      if (ev.city) metaParts.push(ev.province ? (ev.city + ', ' + ev.province) : ev.city);
      // filtered: a scan with no timestamp would leave a trailing " · "
      metaParts.push(formatEventTime(ev.occurred_at));
      metaParts = metaParts.filter(Boolean);
      list.appendChild(el('li', { class: 'wd-timeline__item' + (index === 0 ? ' is-current' : '') }, [
        el('span', { class: 'wd-timeline__rail' }, [
          el('span', { class: 'wd-timeline__dot' }),
          el('span', { class: 'wd-timeline__connector' })
        ]),
        el('div', {}, [
          el('p', { class: 'wd-timeline__status', text: describeEvent(ev, carrier) }),
          el('p', { class: 'wd-timeline__meta', text: metaParts.join(' · ') })
        ])
      ]));
    });
    return el('div', { class: 'wd-card' }, [
      el('p', { class: 'wd-card__title', text: S.shipped.timelineTitle }),
      el('p', { class: 'wd-card__note', text: S.shipped.timelineNote }),
      list
    ]);
  }

  function buildProgressSteps(activeIndex) {
    var labels = [S.processing.step1, S.processing.step2, S.processing.step3, S.processing.step4];
    var bar = el('div', { class: 'wd-progress-steps' });
    labels.forEach(function (label, i) {
      var state = i < activeIndex ? 'is-done' : (i === activeIndex ? 'is-now' : '');
      bar.appendChild(el('div', { class: ('wd-progress-steps__step ' + state).trim() }, [
        el('span', { class: 'wd-progress-steps__bar' }),
        el('span', { class: 'wd-progress-steps__label', text: label })
      ]));
    });
    return bar;
  }

  function buildBringCard(order) {
    return el('div', { class: 'wd-card' }, [
      el('p', { class: 'wd-card__title', text: S.pickup.bringTitle }),
      el('ul', { class: 'wd-bring-list' }, [
        el('li', { text: interpolate(S.pickup.bringOrder, { number: order.number }) }),
        el('li', { text: S.pickup.bringId }),
        el('li', { text: S.pickup.bringOther })
      ])
    ]);
  }

  function buildHoursTable(hoursResponse) {
    var todayDay = hoursResponse.today ? hoursResponse.today.day : null;
    var wrap = el('div', { class: 'wd-store-hours' }, [
      el('p', { class: 'wd-label wd-store-hours__title', text: S.pickup.hoursTitle })
    ]);
    (hoursResponse.hours || []).forEach(function (day) {
      var isToday = day.day === todayDay;
      // Prototype keeps the day name and suffixes it: "Friday · Today".
      // `day` is documented as ISO 1-7; an out-of-range value would otherwise
      // print "undefined · Today" in the hours table.
      var dayName = S.hours.days[day.day - 1] || '';
      var dayLabel = isToday ? ((dayName ? dayName + ' · ' : '') + S.pickup.today) : dayName;
      var timeText;
      if (isToday && hoursResponse.today && hoursResponse.today.special && hoursResponse.today.note) {
        timeText = hoursResponse.today.note;
      } else if (day.closed || !day.periods.length) {
        timeText = isToday ? S.pickup.closedToday : S.hours.closed;
      } else {
        timeText = day.periods.map(function (p) {
          return interpolate(S.hours.openRange, { open: formatClock(p.open), close: formatClock(p.close) });
        }).join(', ');
      }
      wrap.appendChild(el('div', { class: 'wd-store-hours__row' + (isToday ? ' is-today' : '') }, [
        el('span', { text: dayLabel }),
        el('span', { class: 'wd-store-hours__time', text: timeText })
      ]));
    });
    return wrap;
  }

  function buildStoreCard(hoursResponse) {
    var loc = hoursResponse.location;
    var card = el('div', { class: 'wd-card' }, [
      el('p', { class: 'wd-label', text: S.pickup.storeLabel }),
      el('p', { class: 'heading wd-store-card__name', text: loc.name })
    ]);

    // Prototype address block: pin, then street / city / phone as one
    // stack — the phone lives here (plain, tabular), not as its own row;
    // "Call the boutique" below is the tap target.
    var address = loc.address || {};
    var addrLines = [address.line1, address.line2 || addressCityLine(address)].filter(Boolean);
    var addrEl = el('address', { class: 'wd-store-card__address', style: 'font-style:normal' });
    addrLines.forEach(function (line, i) {
      if (i > 0) addrEl.appendChild(el('br'));
      addrEl.appendChild(document.createTextNode(line));
    });
    if (loc.phone) {
      addrEl.appendChild(el('br'));
      // The reference makes the number itself dialable (`.tk-store__addr a`
      // with a tel: href) alongside the "Call store" button below. Falls back
      // to plain text when the API has no dialable form of the number.
      addrEl.appendChild(loc.phone_tel
        ? el('a', { class: 'wd-store-card__phone', href: 'tel:' + loc.phone_tel, text: loc.phone })
        : document.createTextNode(loc.phone));
    }
    card.appendChild(el('div', { class: 'wd-store-card__pin-row' }, [icon('pin'), addrEl]));

    // Only when there ARE hours. A minimal card (see renderPickup) carries a
    // boutique name and nothing else, and an empty "Store hours" heading with
    // no rows under it reads as data that failed to load.
    var hours = (hoursResponse.hours || []).length ? buildHoursTable(hoursResponse) : null;
    // One shared "Locations" page for every boutique when the section is set
    // that way (CONFIG.storePageUrl, already locale-resolved by Liquid);
    // otherwise this location's own page, localized here. Null on neither.
    var storeHref = CONFIG.storePageUrl ? CONFIG.storePageUrl : (loc.page_url ? localizePath(loc.page_url) : null);
    if (hours && storeHref) {
      hours.appendChild(el('a', { class: 'wd-store-card__link', href: storeHref, text: S.pickup.storeLink }));
    }
    if (hours) card.appendChild(hours);

    var actions = el('div', { class: 'wd-store-card__actions' });
    if (loc.map_url) actions.appendChild(el('a', { class: 'button button--primary', href: loc.map_url, target: '_blank', rel: 'noopener noreferrer', text: S.pickup.directions }));
    if (loc.phone_tel) actions.appendChild(el('a', { class: 'button button--secondary', href: 'tel:' + loc.phone_tel, text: S.pickup.callStore }));
    if (actions.childNodes.length) card.appendChild(actions);

    return card;
  }

  /* ------------------------------------------------------------------------
     Per-shipment rendering — the heart of the branching. `shipment.stage`
     and `shipment.pending` / `shipment.simplified` decide everything; the
     wrapping status (shipped / split_shipment / store_pickup) never does.
     ------------------------------------------------------------------------ */

  function shipmentBadge(shipment) {
    if (shipment.pending) return { text: S.split.pendingBadge, tone: 'oxide' };
    if (shipment.stage === 'exception') return { text: S.exception.badge, tone: 'error' };
    if (shipment.stage === 'delivered') return { text: S.delivered.badge, tone: 'success' };
    if (shipment.simplified) {
      return shipment.estimated_delivery_date
        ? { text: S.shipped.badge, tone: 'accent' }
        : { text: S.shippedAltCarrier.badge, tone: 'accent' };
    }
    return { text: S.shipped.badge, tone: 'accent' };
  }

  function titleForShipment(shipment) {
    if (shipment.stage === 'exception') return S.exception.title;
    if (shipment.stage === 'delivered') return S.delivered.title;
    // Both simplified titles name the carrier ("It's with Purolator."), so
    // without one they would read "It's with ." — an order fulfilled by hand
    // in Shopify has no carrier at all and is a documented real case. The
    // carrier-free headline says the same thing without the hole.
    if (shipment.simplified && shipment.carrier) {
      return shipment.estimated_delivery_date
        ? interpolate(S.shippedSimplified.title, { carrier: shipment.carrier })
        : interpolate(S.shippedAltCarrier.title, { carrier: shipment.carrier });
    }
    return S.shipped.title;
  }

  /* Screen 4's two variants order themselves differently (both prototype
     frames): with a service estimate (Purolator) it's anchor → source note →
     facts card; with an alt carrier (GLS) the facts card leads and the
     source note follows it. Either way the explainer body and the primary
     pink CTA live INSIDE the facts card. */
  function buildSimplifiedNodes(shipment) {
    var carrier = shipment.carrier || '';
    var hasAnchor = !!shipment.estimated_delivery_date;
    // Every explainer here names the carrier. With no carrier they degrade to
    // "Scan updates land here as soon as  posts them", so they are dropped
    // instead — the badge, the title and the items still say what is going on.
    var facts = buildShipmentFacts(shipment, {
      ctaLabel: interpolate(S.shippedSimplified.cta, { carrier: carrier }),
      ctaPrimary: true,
      note: !carrier ? null : (hasAnchor
        ? interpolate(S.shippedSimplified.body, { carrier: carrier })
        : interpolate(S.shippedAltCarrier.body, { carrier: carrier }))
    });
    if (hasAnchor) {
      return [
        buildAnchor(formatAnchorDate(shipment), S.shipped.anchorLabel, shipment.service),
        carrier ? buildSourceNote(interpolate(S.shippedSimplified.source, { carrier: carrier })) : null,
        facts
      ].filter(Boolean);
    }
    return [facts, carrier ? buildSourceNote(S.shippedAltCarrier.source) : null].filter(Boolean);
  }

  /* `itemsNode` is only passed by the split-shipment parcels, where the
     prototype slots the parcel's items between the facts card and the
     timeline. The single-shipment screen keeps its items in the aside. */
  function buildTimelineNodes(shipment, itemsNode) {
    var carrier = shipment.carrier || '';
    var events = shipment.events || [];
    var nodes = [];
    if (shipment.estimated_delivery_date) nodes.push(buildAnchor(formatAnchorDate(shipment), S.shipped.anchorLabel, shipment.service));
    // The source note exists to attribute the timeline to a carrier's feed;
    // with no carrier it reads "come straight from 's tracking feed".
    if (carrier) nodes.push(buildSourceNote(interpolate(S.shipped.source, { carrier: carrier, updated: formatUpdatedAgo(shipment.updated_at) })));
    nodes.push(buildShipmentFacts(shipment, { ctaLabel: interpolate(S.shipped.carrierCta, { carrier: carrier }) }));
    if (itemsNode) nodes.push(itemsNode);
    if (events.length) {
      nodes.push(buildTimeline(events, carrier));
    } else if (carrier) {
      // A parcel can be shipped with no scans posted yet (a label printed an
      // hour ago, or ShipStation's per-request budget spent) — an empty
      // "Tracking history" box reads as broken, so say what's happening
      // instead, with the same copy the simplified card uses. Needs a carrier
      // to name, so a hand-fulfilled parcel simply shows no note.
      nodes.push(el('p', { class: 'wd-card__note', text: interpolate(S.shippedSimplified.body, { carrier: carrier }) }));
    }
    return nodes.filter(Boolean);
  }

  function bodyForShipment(shipment, itemsNode) {
    if (shipment.pending) {
      // Prototype pending parcel: bold status line + muted body, nothing else.
      return [
        el('p', { class: 'wd-parcel__status', text: S.split.pendingStatus }),
        el('p', { class: 'wd-parcel__note', text: S.split.pendingBody })
      ];
    }
    if (shipment.stage === 'exception') {
      return [
        el('div', { class: 'banner banner--error' }, [
          el('span', { class: 'banner__ribbon' }, [icon('formError')]),
          el('p', { class: 'banner__content', text: shipment.exception_description || S.exception.lead })
        ]),
        el('div', { class: 'button-wrapper' }, [
          el('a', { class: 'button button--secondary', href: CONFIG.contactUrl, text: S.exception.cta })
        ])
      ];
    }
    if (shipment.stage === 'delivered') {
      var currentEvent = shipment.events && shipment.events[0];
      var lead = shipment.actual_delivery_date
        ? interpolate(S.delivered.lead, { date: formatLongDate(shipment.actual_delivery_date, true) })
        : S.delivered.leadNoDate;
      var nodes = [el('p', { class: 'text--large', text: lead })];
      if (currentEvent && currentEvent.signer) {
        nodes.push(el('p', { class: 'text--subdued', text: interpolate(S.delivered.signedBy, { signer: currentEvent.signer }) }));
      }
      return nodes;
    }
    return shipment.simplified ? buildSimplifiedNodes(shipment) : buildTimelineNodes(shipment, itemsNode);
  }

  /* ------------------------------------------------------------------------
     Screens
     ------------------------------------------------------------------------ */

  var hasRenderedOnce = false;

  /* Set while a stored lookup is being replayed on page load. The screens go
     loading -> result exactly as they do after a submit, but the customer did
     not press anything to start it, so moving focus to the heading would yank
     the page out from under somebody who has only just arrived. */
  var restoringOnLoad = false;

  /* `narrow: true` marks the single-column screens (lookup, invalid,
     rate-limited, error) so the CSS can centre the breadcrumb with the
     620px column, prototype-style. The wide result screens clear it.
     Loading passes nothing and keeps whatever the previous screen set,
     so a refresh from a wide screen doesn't jump the breadcrumb around. */
  function renderScreen(node, narrow) {
    var section = root.closest('.wd-track-order-section');
    if (section && narrow === true) section.setAttribute('data-wd-narrow', '');
    if (section && narrow === false) section.removeAttribute('data-wd-narrow');
    root.innerHTML = '';
    root.setAttribute('data-wd-ready', '');
    root.appendChild(node);
    if (hasRenderedOnce && !restoringOnLoad) {
      var heading = node.querySelector('#wd-track-order-heading');
      if (heading) heading.focus();
    }
    hasRenderedOnce = true;
  }

  /* Prototype field anatomy: a small uppercase label ABOVE the input and an
     example value as the placeholder inside it — the theme's block-label
     variant, not its floating-label one. */
  function buildField(opts) {
    var input = el('input', {
      type: 'text',
      id: opts.id,
      name: opts.name,
      class: 'input__field',
      placeholder: opts.placeholder || '',
      inputmode: opts.inputmode,
      autocomplete: opts.autocomplete,
      maxlength: opts.maxlength,
      required: true,
      value: opts.value || '',
      'aria-invalid': opts.error ? 'true' : null,
      'aria-describedby': opts.error ? 'wd-track-order-invalid-summary' : null
    });

    /* Rewrites the value as it is typed. Guarded on "did this actually
       change anything" so the caret is only ever moved when it has to be —
       reassigning an identical value would still jump it to the end mid-word. */
    if (opts.transform) {
      input.addEventListener('input', function () {
        var next = opts.transform(input.value);
        if (next === input.value) return;
        var atEnd = input.selectionStart === input.value.length;
        input.value = next;
        if (atEnd) input.setSelectionRange(next.length, next.length);
      });
    }

    var wrap = el('div', { class: 'input' + (opts.error ? ' wd-input--error' : '') }, [
      el('label', { for: opts.id, class: 'input__block-label wd-field-label', text: opts.label }),
      input
    ]);
    return { wrap: wrap, input: input };
  }

  function buildLookupFields(prefill) {
    prefill = prefill || {};
    return {
      order: buildField({
        id: 'wd-track-order-number', name: 'order_number', label: S.lookup.orderLabel,
        placeholder: S.lookup.orderPlaceholder, inputmode: 'numeric', autocomplete: 'off',
        maxlength: '8', transform: digitsOnly,
        value: prefill.orderNumber, error: prefill.error
      }),
      postal: buildField({
        id: 'wd-track-postal-code', name: 'postal_code', label: S.lookup.postalLabel,
        placeholder: S.lookup.postalPlaceholder, inputmode: 'text', autocomplete: 'postal-code',
        maxlength: '7', transform: formatPostal,
        value: prefill.postalCode, error: prefill.error
      })
    };
  }

  /* V2 lookup note 2: the two fields stack at EVERY width. They used to sit
     side by side on desktop via the theme's .input-row grid. */
  function buildFieldRow(fields) {
    return el('div', { class: 'wd-lookup__fields' }, [fields.order.wrap, fields.postal.wrap]);
  }

  /* The "Can't find either one?" line sits OUTSIDE the centred 620px column,
     flush with the page gutter — the prototype's lookup and not-found frames
     both park it at the container's left edge on desktop. */
  function buildAltLine(text, linkText) {
    return el('p', { class: 'wd-lookup__alt' }, [
      document.createTextNode(text + ' '),
      el('a', { class: 'link', href: CONFIG.contactUrl, text: linkText })
    ]);
  }

  /* `prefill` fills the two fields in without marking them as errors — used
     when a resumed lookup could not be completed, so the customer sends it
     again with one tap rather than typing it out. renderInvalid() has its own
     prefill, with the error styling, for the case where the details were
     genuinely wrong. */
  function renderLookup(prefill, notice) {
    var narrow = el('div', { class: 'wd-lookup' });
    narrow.appendChild(buildHead({ title: S.lookup.title, lead: S.lookup.sub }));

    if (notice) narrow.appendChild(el('p', { class: 'wd-lookup__notice', text: notice }));

    var fields = buildLookupFields(prefill);
    var form = el('form', { class: 'form' }, [
      buildFieldRow(fields),
      el('div', { class: 'wd-lookup__ctas' }, [
        el('button', { type: 'submit', is: 'loader-button', class: 'form__submit button button--primary wd-lookup__submit' }, [S.lookup.cta])
      ]),
      el('p', { class: 'wd-lookup__hint', text: S.lookup.hint })
    ]);
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var orderNumber = fields.order.input.value.trim();
      var postalCode = fields.postal.input.value.trim();
      if (!orderNumber || !postalCode) return;
      submitLookup(orderNumber, postalCode);
    });

    narrow.appendChild(el('div', { class: 'wd-card wd-lookup__card' }, [form]));

    var screen = el('div', { class: 'wd-track-order__screen' }, [
      narrow,
      buildAltLine(S.lookup.alt, S.lookup.altLink)
    ]);
    renderScreen(screen, true);
  }

  /* V2 loading note 1: the branded wait between submit and result — the JP
     scottie, a bubblegum progress bar and the page's own off-white, replacing
     the bare spinner. The bar is indeterminate on purpose: the lookup's length
     depends on ShipStation and cannot be predicted, and a fake percentage
     would be a lie. */
  function renderLoading() {
    renderScreen(el('div', { class: 'wd-track-order__screen wd-loading' }, [
      el('div', { class: 'wd-loading__inner' }, [
        icon('dog', 'wd-loading__dog'),
        el('p', { class: 'wd-loading__title', text: S.loading.title }),
        el('div', { class: 'wd-loading__bar' }, [el('span')]),
        el('p', { class: 'wd-loading__note', text: S.loading.note })
      ])
    ]));
  }

  function renderInvalid(meta) {
    var narrow = el('div', { class: 'wd-lookup' });
    narrow.appendChild(buildHead({ back: true, tone: 'outline', badgeText: S.invalid.badge, title: S.invalid.title }));
    narrow.appendChild(el('p', { id: 'wd-track-order-invalid-summary', class: 'wd-track-order__lead', text: S.invalid.lead }));
    narrow.appendChild(el('ul', { class: 'wd-reason-list' }, [
      el('li', { text: S.invalid.reason1 }),
      el('li', { text: S.invalid.reason2 })
    ]));

    var fields = buildLookupFields({ orderNumber: meta.orderNumber, postalCode: meta.postalCode, error: true });
    var form = el('form', { class: 'form' }, [
      buildFieldRow(fields),
      el('div', { class: 'wd-lookup__ctas' }, [
        el('button', { type: 'submit', class: 'button button--primary wd-lookup__submit' }, [S.invalid.cta]),
        el('a', { class: 'button button--secondary', href: CONFIG.contactUrl }, [S.invalid.cta2])
      ]),
      // Same explainer as the lookup card — the prototype repeats it here.
      el('p', { class: 'wd-lookup__hint', text: S.lookup.hint })
    ]);
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var orderNumber = fields.order.input.value.trim();
      var postalCode = fields.postal.input.value.trim();
      if (!orderNumber || !postalCode) return;
      submitLookup(orderNumber, postalCode);
    });

    narrow.appendChild(el('div', { class: 'wd-card wd-lookup__card' }, [form]));

    var screen = el('div', { class: 'wd-track-order__screen' }, [
      narrow,
      buildAltLine(S.invalid.alt, S.invalid.altLink)
    ]);
    renderScreen(screen, true);
  }

  /* The two undesigned screens. They get the back link as well: without it a
     rate-limited or offline visitor has no route back to the form except
     reloading the page, and "Contact us" is not an answer to "try again in a
     minute". */
  function renderRateLimited() {
    var screen = el('div', { class: 'wd-track-order__screen wd-lookup' });
    screen.appendChild(buildHead({ back: true, title: S.rateLimited.title, lead: S.rateLimited.lead }));
    screen.appendChild(el('div', { class: 'button-wrapper' }, [
      el('a', { class: 'button button--secondary', href: CONFIG.contactUrl, text: S.invalid.cta2 })
    ]));
    renderScreen(screen, true);
  }

  function renderError() {
    var screen = el('div', { class: 'wd-track-order__screen wd-lookup' });
    screen.appendChild(buildHead({ back: true, title: S.error.title, lead: S.error.lead }));
    screen.appendChild(el('div', { class: 'button-wrapper' }, [
      el('a', { class: 'button button--secondary', href: CONFIG.contactUrl, text: S.invalid.cta2 })
    ]));
    renderScreen(screen, true);
  }

  /* The aside every result screen shares: the order's items, the refunded
     block when there is one, then the help card. Keeping it in one place is
     what stops the refund block appearing on some screens and not others. */
  function buildResultAside(order, extraNodes) {
    var aside = [buildItemList(order.items || [])];

    if ((order.refunded_items || []).length) {
      aside.push(buildRefundedBlock(order.refunded_items, partialRefundCopy(order.refunded_items.length)));
    }

    (extraNodes || []).forEach(function (node) { aside.push(node); });
    aside.push(buildHelpCard());
    return aside;
  }

  function renderProcessing(data, stepIndex) {
    var order = data.order;
    var screen = el('div', { class: 'wd-track-order__screen' });
    screen.appendChild(buildHead({
      back: true,
      tone: 'oxide', badgeText: S.processing.badge, title: S.processing.title, lead: S.processing.lead,
      meta: buildOrderMeta(order)
    }));

    var stepsCard = el('div', { class: 'wd-card' }, [
      buildProgressSteps(stepIndex),
      el('p', { class: 'wd-card__note wd-progress-steps__note', text: S.processing.stepNote }),
      buildSourceNote(S.processing.source)
    ]);

    // The processing screen keeps its items in the MAIN column (the aside
    // carries the shipping address instead), so the refunded block follows
    // them there rather than coming from buildResultAside.
    var main = [stepsCard, buildItemList(order.items || [])];

    if ((order.refunded_items || []).length) {
      main.push(buildRefundedBlock(order.refunded_items, partialRefundCopy(order.refunded_items.length)));
    }

    screen.appendChild(buildTwoColumnLayout(
      main,
      [buildShipToCard(order.shipping_address), buildHelpCard()]
    ));

    renderScreen(screen, false);
  }

  function renderSingleShipment(data) {
    var order = data.order;
    var shipment = (data.shipments || [])[0];
    if (!shipment) { renderError(); return false; }

    if (shipment.stage === 'delivered') { renderDelivered(data, shipment); return true; }

    var badge = shipmentBadge(shipment);
    var extraMeta = shipment.shipped_at ? { label: S.shared.shippedLabel, value: formatShortDate(shipment.shipped_at) } : null;

    var screen = el('div', { class: 'wd-track-order__screen' });
    screen.appendChild(buildHead({
      back: true,
      tone: badge.tone, badgeText: badge.text, title: titleForShipment(shipment),
      meta: buildOrderMeta(order, extraMeta)
    }));

    screen.appendChild(buildTwoColumnLayout(
      bodyForShipment(shipment),
      buildResultAside(order)
    ));

    renderScreen(screen, false);
    return true;
  }

  /* V2 delivered notes 1-3. A screen of its own now: the delivery date (and the
     time only when the carrier sent one), the scan history, and the collapsed
     missing-parcel block underneath. Deliberately says nothing about WHERE it
     was left — the feed does not tell us whether that was the door, the mailbox
     or the mail room, and guessing is worse than silence. */
  function renderDelivered(data, shipment) {
    var order = data.order;
    var carrier = shipment.carrier || '';
    var moment = deliveryMoment(shipment);

    var lead;
    if (!moment) {
      lead = S.delivered.leadNoDate;
    } else if (moment.hasTime) {
      lead = interpolate(S.delivered.leadWithTime, {
        date: formatLongDate(moment.date.toISOString(), false),
        time: formatTimeOfDay(moment.date)
      });
    } else {
      lead = interpolate(S.delivered.lead, { date: formatLongDate(shipment.actual_delivery_date, true) });
    }

    var screen = el('div', { class: 'wd-track-order__screen' });
    screen.appendChild(buildHead({
      back: true,
      tone: 'oxide', badgeText: S.delivered.badge, title: S.delivered.title, lead: lead,
      meta: buildOrderMeta(order, shipment.shipped_at
        ? { label: S.shared.shippedLabel, value: formatShortDate(shipment.shipped_at) }
        : null)
    }));

    var main = [];

    if (moment) {
      main.push(buildAnchor(
        formatLongDate(moment.date.toISOString(), false),
        S.delivered.deliveredLabel,
        moment.hasTime ? formatTimeOfDay(moment.date) : null
      ));
    }

    main.push(buildSourceNote(interpolate(S.delivered.source, { carrier: carrier })));

    var currentEvent = (shipment.events || [])[0];
    if (currentEvent && currentEvent.signer) {
      main.push(el('p', { class: 'wd-card__note', text: interpolate(S.delivered.signedBy, { signer: currentEvent.signer }) }));
    }

    if ((shipment.events || []).length) main.push(buildTimeline(shipment.events, carrier));
    if (moment) main.push(buildMissingBlock(order, shipment, moment));

    screen.appendChild(buildTwoColumnLayout(main, buildResultAside(order)));
    renderScreen(screen, false);
  }

  /* V2 cancelled notes 1-3. A cancelled order used to land on the "we couldn't
     match that" screen, because the API reported it as `invalid`; it now has
     its own status and this screen. A PARTIAL refund is NOT this screen — that
     stays on whichever tracking screen the order was already on, with the
     refunded piece in its own block. */
  function renderCancelled(data) {
    var order = data.order;
    if (!order) { renderError(); return false; }

    var screen = el('div', { class: 'wd-track-order__screen' });
    screen.appendChild(buildHead({
      back: true,
      tone: 'oxide', badgeText: S.cancelled.badge, title: S.cancelled.title, lead: S.cancelled.lead,
      meta: buildOrderMeta(order)
    }));

    // Everything on a cancelled order is refunded. When the refund has already
    // been recorded in Shopify the lines arrive in refunded_items; when the
    // order was cancelled without one they are still in items, and they are
    // shown the same way — nothing is coming either way.
    var refunded = (order.refunded_items || []).length ? order.refunded_items : (order.items || []);

    screen.appendChild(buildTwoColumnLayout(
      [buildRefundCard(order.original_total || order.total)],
      [
        buildRefundedBlock(refunded, { title: S.refund.refundedTitle }),
        buildHelpCard()
      ]
    ));

    renderScreen(screen, false);
    return true;
  }

  /* V2 shipped note 3: "the two-column layout is now identical in both states
     — on a split shipment the whole block is duplicated per shipment, items
     stay in the right column, nothing moves below."

     So a parcel is no longer a flat stack with its items inline; it is the same
     main/aside grid the single-parcel screen uses, wrapped in one bordered
     block with the "SHIPMENT 1 OF 2" header on top. */
  function buildParcelArticle(shipment) {
    var badge = shipmentBadge(shipment);
    var article = el('article', { class: 'wd-parcel' + (shipment.pending ? ' wd-parcel--pending' : '') });

    // Prototype header line: "SHIPMENT 1 OF 2 · 2 ITEMS" left, badge right.
    // Line count, not unit sum — the prototype's qty-2 tank parcel says "1 item".
    var itemLines = (shipment.items || []).length;
    var headerText = interpolate(S.split.shipmentOf, { index: String(shipment.index), of: String(shipment.of) });
    if (itemLines > 0) headerText += ' · ' + itemsLabel(itemLines);
    article.appendChild(el('div', { class: 'wd-parcel__header' }, [
      el('p', { class: 'wd-label', text: headerText }),
      el('span', { class: 'wd-status-badge wd-status-badge--' + badge.tone, text: badge.text })
    ]));

    // No itemsNode passed: the parcel's items belong in this block's own right
    // column now, not slotted into the middle of the main column.
    var aside = itemLines ? [buildItemList(shipment.items, true)] : [];
    article.appendChild(buildTwoColumnLayout(bodyForShipment(shipment), aside));

    return article;
  }

  function renderSplitShipment(data) {
    var order = data.order;

    // Parcel items arrive with price: null (the contract points at
    // order.items for prices), but the prototype's parcel lists SHOW prices —
    // so graft them back on by title+variant before rendering.
    var priceByKey = {};
    (order.items || []).forEach(function (item) {
      if (item.price) priceByKey[item.title + ' ' + (item.variant || '')] = item.price;
    });
    (data.shipments || []).forEach(function (shipment) {
      (shipment.items || []).forEach(function (item) {
        if (!item.price) item.price = priceByKey[item.title + ' ' + (item.variant || '')] || null;
      });
    });

    var screen = el('div', { class: 'wd-track-order__screen' });
    // Prototype: the split screen carries the same "On its way" badge as
    // the single-shipment one, with the two-parcels note as the lead.
    screen.appendChild(buildHead({
      back: true,
      tone: 'accent', badgeText: S.shipped.badge,
      title: S.shipped.title, lead: S.split.note, meta: buildOrderMeta(order)
    }));
    (data.shipments || []).forEach(function (shipment) {
      screen.appendChild(buildParcelArticle(shipment));
    });

    // A refund on a split order sits under the parcels, before the help card —
    // it belongs to the order, not to either parcel.
    if ((order.refunded_items || []).length) {
      screen.appendChild(buildRefundedBlock(order.refunded_items, partialRefundCopy(order.refunded_items.length)));
    }

    screen.appendChild(buildHelpCard());
    renderScreen(screen, false);
  }

  function renderPickup(data, hoursResponse) {
    var order = data.order;
    // `pickup` is only null here on a malformed payload, but a status of
    // store_pickup with no pickup block must degrade to the not-ready screen
    // rather than throwing the whole render away.
    var pickup = data.pickup || {};
    var ready = !!pickup.ready;

    var badgeText = ready ? S.pickup.badge : S.pickup.notReadyBadge;
    var badgeTone = ready ? 'accent' : 'oxide';
    var title = ready ? S.pickup.title : S.pickup.titleNotReady;
    var lead = ready
      ? (pickup.ready_at ? interpolate(S.pickup.lead, { date: formatLongDate(pickup.ready_at, false) }) : S.pickup.leadNoDate)
      : S.pickup.notReadyLead;

    var screen = el('div', { class: 'wd-track-order__screen' });
    screen.appendChild(buildHead({
      back: true,
      tone: badgeTone, badgeText: badgeText, title: title, lead: lead,
      meta: buildOrderMeta(order)
    }));

    var mainNodes = [];
    // The full card needs /store-hours, which needs the boutique to resolve to
    // a configured handle. When it does not, the payload still carries the
    // boutique NAME — so say at least that. Telling somebody their order is
    // waiting without telling them where is the worst version of this screen.
    if (hoursResponse) {
      mainNodes.push(buildStoreCard(hoursResponse));
    } else if (pickup.location && pickup.location.name) {
      mainNodes.push(buildStoreCard({ location: { name: pickup.location.name } }));
    }
    mainNodes.push(buildBringCard(order));

    screen.appendChild(buildTwoColumnLayout(mainNodes, buildResultAside(order)));

    renderScreen(screen, false);
  }

  /* ------------------------------------------------------------------------
     Resuming a lookup
     ------------------------------------------------------------------------
     A result used to live and die with one page view. Clicking anything that
     left the page — "Contact us" in the help card, the boutique's store page —
     and pressing Back landed the customer back on an empty form with their
     order number to type in again. Opening those links in a new tab was a
     patch around that, not a fix for it.

     So a SUCCESSFUL lookup is now remembered on the customer's own device and
     replayed on the next page view: Back works, a reload works, and somebody
     coming back tomorrow to see whether it moved gets their status without
     re-entering anything.

     ⚠️ WHAT IS STORED IS THE TWO LOOKUP FIELDS AND NOTHING ELSE — the order
     number and the postal code the customer typed, plus a timestamp. THE
     RESPONSE IS NEVER STORED: not the shipping address, not the e-mail or
     phone from order.contact, not the items, not the tracking history. The
     contract's rule that the address must not reach anything outliving the
     render is untouched by this, and it is exactly why a resume re-FETCHES
     instead of redrawing something cached.

     Re-fetching is the only correct behaviour on its own terms anyway: the
     whole point of coming back tomorrow is to see a status that has MOVED. A
     cached render would confidently show yesterday's answer.

     WHY localStorage AND NOT sessionStorage: sessionStorage dies with the tab.
     It can fix the Back button, but it cannot survive until tomorrow, and the
     next-day return is half of what this is for.

     WHY NOT THE URL. The credential here is a postal code, and what it unlocks
     carries the customer's name, e-mail, phone and full shipping address. A URL
     holding an order number and its postal code is a permanent bearer token to
     all of that — sitting in browser history, the referrer header and the
     server access log, on whatever device was used and outside our reach.
     Order numbers are sequential, so it would double as a template for
     guessing them. The same reasoning already ruled a query string out for the
     claim handoff; see stashClaimPrefill().

     The window is a theme setting ("Remember a lookup for"), and "Off" stores
     nothing at all.
     ------------------------------------------------------------------------ */

  var RESUME_KEY = 'wd-track-resume';

  /* How long a replayed lookup may sit on the loading screen before the form
     comes back instead. Only the resume path is bounded: a submit is something
     the customer is waiting on deliberately, but an arrival is not, and a slow
     or unreachable API must never leave a returning visitor staring at a
     spinner where they used to get a usable form straight away. */
  var RESUME_TIMEOUT_MS = 10 * 1000;

  /* The setting, in ms. Missing, zero or nonsense disables the feature — a bad
     value has to fail closed and store nothing. */
  function resumeTtlMs() {
    var hours = Number(CONFIG.resumeTtlHours);
    if (!isFinite(hours) || hours <= 0) return 0;
    return hours * 3600 * 1000;
  }

  function clearResume() {
    try { window.localStorage.removeItem(RESUME_KEY); } catch (e) {}
  }

  /* Called ONLY where a real order was drawn — never for `invalid`, a
     rate-limited reply or a network error. Replaying a lookup that failed
     would re-spend the 30-failed-per-hour-per-IP allowance on every page view
     and could lock a customer out of the very form they need.

     The timestamp is rewritten on every successful resume, so the window runs
     from the last time the page was used rather than from the first lookup —
     somebody checking daily is exactly who this is for. */
  function saveResume(orderNumber, postalCode) {
    if (!resumeTtlMs()) { clearResume(); return; }

    try {
      window.localStorage.setItem(RESUME_KEY, JSON.stringify({
        at: Date.now(),
        order: orderNumber,
        postal: postalCode
      }));
    } catch (e) {
      // Storage blocked or full. The page behaves exactly as it did before.
    }
  }

  /* The stored lookup, or null. Anything expired, unreadable, or not shaped
     the way it was written is deleted rather than repaired: a resume is a
     convenience, and a broken one must never stand between a customer and the
     form. */
  function readResume() {
    var ttl = resumeTtlMs();
    if (!ttl) { clearResume(); return null; }

    var raw;
    try { raw = window.localStorage.getItem(RESUME_KEY); } catch (e) { return null; }
    if (!raw) return null;

    var data;
    try { data = JSON.parse(raw); } catch (e) { clearResume(); return null; }

    var usable = data && typeof data === 'object' &&
      typeof data.order === 'string' && data.order &&
      typeof data.postal === 'string' && data.postal &&
      typeof data.at === 'number' && (Date.now() - data.at) <= ttl;

    if (!usable) { clearResume(); return null; }

    return { order: data.order, postal: data.postal };
  }

  /* ------------------------------------------------------------------------
     Shareable links
     ------------------------------------------------------------------------
     A resume is per-device by design, so it cannot answer "send me the
     tracking page". A share link can, and it is the ONE place an order number
     and a postal code are allowed into a URL — so everything here exists to
     keep that exposure as small as it can be while still working:

       - the link is only ever produced by an explicit "Copy tracking link"
         press, never by the page leaving one sitting in the address bar;
       - arriving on one, the query is stripped from the URL immediately,
         before the lookup is even sent, so it does not linger on screen, ride
         along in the next Referer header, or get re-copied by accident;
       - the page asks not to be indexed the moment it sees the parameters;
       - the link carries its own expiry and stops working on its own.

     ⚠️ WHAT THIS IS NOT. A share link IS a password: whoever holds it can see
     the order — name, e-mail, phone and address included — until it expires,
     and it cannot be revoked. That is the accepted trade for shareability
     (client feedback, Sept 2026), NOT an oversight. Two consequences follow,
     and neither is optional:

       - AN EXPIRED LINK IS NEVER PREFILLED PAST. It lands on an empty form,
         not on the two fields filled in, or expiry would sit one tap away
         from meaningless.
       - hashing the postal code would not help, and is deliberately not
         attempted. A hash made in the page cannot be checked by the server,
         and there are only about 900,000 Canadian postal codes, so a hash of
         one is guessed almost immediately. Only a server-issued token fixes
         this properly; see the note in the section schema.
     ------------------------------------------------------------------------ */

  var SHARE_ORDER = 'o';
  var SHARE_POSTAL = 'p';
  var SHARE_EXP = 'exp';

  /* Generous next to the form's own 8 and 7 character limits, but bounded:
     without this, a hand-made link could push kilobytes through the lookup
     endpoint, and nothing that long was ever going to match an order. */
  var SHARE_VALUE_MAX = 32;

  /* Slack on the expiry check, purely for clock differences between the device
     that wrote the link and the one reading it. */
  var SHARE_CLOCK_SLACK_MS = 24 * 3600 * 1000;

  /* Sharing rides on the same window as the resume, so turning remembering off
     turns sharing off with it — one switch that means "keep nothing". */
  function shareEnabled() {
    return !!(CONFIG.shareLinks && resumeTtlMs());
  }

  function buildShareUrl(orderNumber, postalCode) {
    var expiresAt = Math.floor((Date.now() + resumeTtlMs()) / 1000);

    return window.location.origin + window.location.pathname +
      '?' + SHARE_ORDER + '=' + encodeURIComponent(orderNumber) +
      '&' + SHARE_POSTAL + '=' + encodeURIComponent(postalCode) +
      '&' + SHARE_EXP + '=' + expiresAt;
  }

  /* Asks robots to leave this page view alone. Only ever called once a share
     link has actually been seen, so the ordinary lookup page — which is worth
     finding in a search — stays indexable. */
  function denyIndexing() {
    try {
      var meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', 'noindex, nofollow');
      document.head.appendChild(meta);
    } catch (e) {}
  }

  /* Drops the query without adding a history entry or disturbing the state on
     the one we are standing on. */
  function stripQuery() {
    try {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.hash
      );
    } catch (e) {}
  }

  /* Reads an incoming share link and CONSUMES it — the noindex and the URL
     strip happen here, on sight, whether or not the link turns out to be
     usable. Returns null when there is no link at all. */
  function takeShareLink() {
    var search = window.location.search;
    if (!search || search.length < 2) return null;

    var params;
    try { params = new URLSearchParams(search); } catch (e) { return null; }

    var order = params.get(SHARE_ORDER);
    var postal = params.get(SHARE_POSTAL);
    if (!order || !postal) return null;

    var withinBounds = order.length <= SHARE_VALUE_MAX && postal.length <= SHARE_VALUE_MAX;

    denyIndexing();
    stripQuery();

    /* An expiry is REQUIRED, not merely honoured when present: a link without
       one would never stop working, and a hand-written link must not be able
       to opt out of the window by leaving the parameter off. It is capped to
       the window the theme is set to as well, so shortening that setting
       retires links that are already out there. */
    var expiresAt = parseInt(params.get(SHARE_EXP), 10) * 1000;
    var ceiling = Date.now() + resumeTtlMs() + SHARE_CLOCK_SLACK_MS;
    var usable = shareEnabled() && withinBounds && isFinite(expiresAt) &&
      expiresAt > Date.now() && expiresAt <= ceiling;

    return { order: order, postal: postal, usable: usable };
  }

  /* The lookup on screen, so the share button can rebuild its link without
     every render function having to carry the postal code down to it. Memory
     only, and only ever the two fields the customer typed. */
  var currentLookup = null;

  /* "48 hours" is fine; "168 hours" is not how anyone says "7 days". Every
     option on the setting is a whole number of days, so anything from two days
     up is said in days — which also means the plural is never in doubt and no
     singular form is needed. */
  function shareNoteText() {
    var hours = Math.round(resumeTtlMs() / 3600000);

    if (hours >= 48 && hours % 24 === 0) {
      return interpolate(S.shared.shareNoteDays, { days: String(hours / 24) });
    }

    return interpolate(S.shared.shareNoteHours, { hours: String(hours) });
  }

  function buildShareRow() {
    if (!shareEnabled() || !currentLookup) return null;

    var button = el('button', {
      type: 'button',
      class: 'wd-share-link',
      text: S.shared.copyLink
    });

    button.addEventListener('click', function () {
      copyToClipboard(
        buildShareUrl(currentLookup.order, currentLookup.postal),
        button,
        { idle: S.shared.copyLink, done: S.shared.copyLinkDone }
      );
    });

    return el('div', { class: 'wd-track-order__share' }, [
      button,
      /* Says plainly what pressing it hands over. Somebody sharing their own
         order is entitled to know the link shows their address for as long as
         it lives. */
      el('p', { class: 'wd-share-note', text: shareNoteText() })
    ]);
  }

  /* ------------------------------------------------------------------------
     History
     ------------------------------------------------------------------------
     Screen swaps used to touch history not at all, so the browser's Back
     button left the tracking page entirely and the in-page "←" was the only
     route to the form. Moving lookup -> result now pushes ONE entry, which
     makes the browser button do what the arrow does.

     The URL never changes. The entry is told apart by a marker in
     history.state, so nothing about the order reaches the address bar, the
     referrer or any log — see the note above on why that matters here.
     ------------------------------------------------------------------------ */

  /* The last drawn order, held in MEMORY ONLY so pressing Forward can redraw
     it without a second lookup. Dies with the page view, like every other copy
     of the response. */
  var lastResult = null;

  /* Identifies THIS page view. A history entry survives a reload, so finding
     `wd: 'result'` in history.state does not by itself mean the entry was
     pushed by the code currently running — coming back from the contact page
     without a bfcache hit lands on exactly that: our marker, a fresh page. The
     difference decides whether history.back() is safe, so the marker carries
     the view that wrote it. */
  var PAGE_VIEW = String(Date.now()) + '-' + Math.random().toString(36).slice(2);

  function onResultEntry() {
    return !!(window.history.state && window.history.state.wd === 'result');
  }

  /* True only when the entry on screen was pushed by this page view — the one
     case where stepping back is guaranteed to land on our own form rather
     than on whatever the customer was looking at before they got here. */
  function pushedByThisView() {
    var state = window.history.state;
    return !!(state && state.wd === 'result' && state.v === PAGE_VIEW);
  }

  function markResultInHistory() {
    // View-agnostic on purpose: a reload that lands on an inherited result
    // entry must not stack a second one on top of it.
    if (onResultEntry()) return;
    try { window.history.pushState({ wd: 'result', v: PAGE_VIEW }, ''); } catch (e) {}
  }

  /* Drops the marker from an inherited entry once the form is drawn over it,
     so the entry describes what is actually on screen. */
  function clearResultMarker() {
    if (!onResultEntry()) return;
    try { window.history.replaceState(null, ''); } catch (e) {}
  }

  /* ------------------------------------------------------------------------
     Networking — every response the API can return for a well-formed
     request is HTTP 200; a non-200 or an unparseable body means something
     is wrong on our end, so it is treated the same as a network failure
     (screen 13), never surfaced as a customer-facing status.
     ------------------------------------------------------------------------ */

  function fetchLookup(orderNumber, postalCode) {
    return fetch(CONFIG.apiBaseUrl + '/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_number: orderNumber, postal_code: postalCode })
    }).then(function (res) {
      if (!res.ok) throw new Error('unexpected status ' + res.status);
      return res.json();
    });
  }

  function fetchStoreHours(handle) {
    var url = CONFIG.apiBaseUrl + '/store-hours?location=' + encodeURIComponent(handle);
    return fetch(url)
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (json) { return (json && json.ok) ? json : null; })
      .catch(function () { return null; });
  }

  /* Returns TRUE when a real order was drawn, FALSE for `invalid`, a
     rate-limited reply, a network-level failure or a status we do not know.
     Callers use that to decide whether the lookup is worth remembering and
     worth a history entry — neither should happen for a screen the customer
     cannot come back to. */
  function renderFromResponse(data, meta) {
    if (!data || typeof data !== 'object') { renderError(); return false; }

    if (data.ok === false) {
      if (data.error === 'rate_limited') { renderRateLimited(); return false; }
      renderError();
      return false;
    }

    switch (data.status) {
      case 'invalid':
        renderInvalid(meta);
        return false;
      // Both pre-shipment statuses render the ONE designed frame: "Order
      // placed" done, "Packing" current. The copy already says "We're
      // packing it up" for both, so a processing order showing step 1 as
      // current matches the reference rather than inventing an undesigned
      // step-0 state.
      case 'processing':
      case 'packing':
        renderProcessing(data, 1);
        return true;
      // A cancelled order used to come back as `invalid` and land on the
      // "we couldn't match that" screen. It has its own status and its own
      // screen now — see the note in JPT_Order::payload() for why that does
      // not reopen the enumeration oracle.
      case 'cancelled':
        return renderCancelled(data);
      case 'shipped':
        return renderSingleShipment(data);
      case 'split_shipment':
        renderSplitShipment(data);
        return true;
      case 'store_pickup': {
        var handle = data.pickup && data.pickup.location && data.pickup.location.handle;
        if (handle) {
          fetchStoreHours(handle).then(function (hoursResponse) { renderPickup(data, hoursResponse); });
        } else {
          renderPickup(data, null);
        }
        // True as soon as the order resolved: the store-hours call is a
        // decoration on a screen that renders either way.
        return true;
      }
      default:
        renderError();
        return false;
    }
  }

  /* ------------------------------------------------------------------------
     Submit — one lookup per explicit form submit, nothing automatic.
     ------------------------------------------------------------------------ */

  var inFlight = false;

  /* Bumped whenever a lookup starts, and whenever the customer navigates away
     from one. A reply whose generation is stale is dropped unrendered.

     Without this, going Forward and then Back while a lookup was in the air
     left the FORM on screen with `inFlight` still true — and the next press of
     "Track it" did nothing at all, silently, with no way for the customer to
     tell that the page had stopped listening. */
  var requestSeq = 0;

  function submitLookup(orderNumber, postalCode) {
    if (inFlight) return;

    var seq = ++requestSeq;
    inFlight = true;
    renderLoading();

    fetchLookup(orderNumber, postalCode).then(function (data) {
      if (seq !== requestSeq) return;
      inFlight = false;
      var meta = { orderNumber: orderNumber, postalCode: postalCode };
      currentLookup = { order: orderNumber, postal: postalCode };

      if (renderFromResponse(data, meta)) {
        lastResult = { data: data, meta: meta };
        saveResume(orderNumber, postalCode);
        markResultInHistory();
      }
    }).catch(function () {
      if (seq !== requestSeq) return;
      inFlight = false;
      renderError();
    });
  }

  /* The stored lookup, replayed. Not a submit and not a poll: it re-runs the
     ONE lookup the customer made themselves, because the response was
     deliberately never stored and the status may well have moved since.

     Every way this can fail lands on the FORM with both fields already filled
     in, rather than on an error screen. The customer has not asked for
     anything yet, so a red screen on arrival answers a question nobody put,
     while a form they can send with one tap is the useful version of the same
     news. The one exception is `invalid`: renderInvalid() draws its own
     screen, and that screen already carries both fields, filled in from
     `meta`. */
  function restoreLookup(orderNumber, postalCode) {
    var prefill = { orderNumber: orderNumber, postalCode: postalCode };
    var settled = false;
    var seq = ++requestSeq;

    /* Returns true when the caller should stop: either the timeout already
       drew a fallback, or the customer has moved on and this reply is stale. */
    function finish() {
      if (settled || seq !== requestSeq) return true;
      settled = true;
      window.clearTimeout(timer);
      inFlight = false;
      return false;
    }

    function fallBackToForm() {
      renderLookup(prefill);
      restoringOnLoad = false;
    }

    inFlight = true;
    restoringOnLoad = true;
    renderLoading();

    var timer = window.setTimeout(function () {
      if (settled || seq !== requestSeq) return;
      settled = true;
      inFlight = false;
      // A late reply is ignored: `settled` is already set, so it cannot
      // redraw over a form the customer may have started typing into.
      fallBackToForm();
    }, RESUME_TIMEOUT_MS);

    fetchLookup(orderNumber, postalCode).then(function (data) {
      if (finish()) return;

      // Rate-limited or malformed: transient, so the stored lookup is KEPT —
      // it will work again in a minute, and tomorrow's visit should not have
      // been forgotten over a busy moment today.
      if (!data || typeof data !== 'object' || data.ok === false) {
        fallBackToForm();
        return;
      }

      /* ⚠️ The render is guarded SEPARATELY from the fetch. `settled` is about
         the timeout race, so a throw in here would reach the .catch below,
         find the attempt already settled, and return having drawn nothing —
         leaving a customer on the loading screen with no way out. A submit
         degrades to renderError() in the same situation; an arrival has the
         form to fall back to, which is more use. */
      try {
        var meta = { orderNumber: orderNumber, postalCode: postalCode };
        currentLookup = { order: orderNumber, postal: postalCode };

        if (renderFromResponse(data, meta)) {
          lastResult = { data: data, meta: meta };
          saveResume(orderNumber, postalCode);
          markResultInHistory();
        } else {
          // The order no longer resolves. It never will again, so stop
          // replaying it on every visit.
          clearResume();
        }

        restoringOnLoad = false;
      } catch (e) {
        fallBackToForm();
      }
    }).catch(function () {
      if (finish()) return;
      fallBackToForm();
    });
  }

  /* Back and Forward across the one pushed entry. */
  window.addEventListener('popstate', function (event) {
    var state = event.state;

    /* The customer has moved. Anything still in the air was asked for from a
       screen they have left, so it is abandoned rather than allowed to land on
       top of wherever they are now — and `inFlight` is released with it, so
       the form they may be looking at still works. */
    requestSeq++;
    inFlight = false;

    if (state && state.wd === 'result') {
      // Forward, onto a result. Redraw from memory when this page view still
      // holds it; otherwise replay the stored lookup.
      if (lastResult) { renderFromResponse(lastResult.data, lastResult.meta); return; }

      var stored = readResume();
      if (stored) { restoreLookup(stored.order, stored.postal); return; }
    }

    renderLookup();
  });

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  /* A share link is read FIRST and always consumed, so the URL is cleaned and
     the noindex is in place even when the link turns out to be too old to use.
     It outranks a stored lookup because following a link is a deliberate act;
     a resume is only ever a convenience.

     An expired one deliberately gets the EMPTY form with a word about why —
     never the fields filled in from the link it arrived on. */
  var shareLink = takeShareLink();
  var storedLookup = readResume();

  if (shareLink) {
    if (shareLink.usable) restoreLookup(shareLink.order, shareLink.postal);
    else renderLookup(null, S.shared.linkExpired);
  } else if (storedLookup) {
    restoreLookup(storedLookup.order, storedLookup.postal);
  } else {
    renderLookup();
  }
})();
